import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Typography, Tag, Modal, Form, Input, Select, message, Alert, Tooltip, Divider, Checkbox, Upload } from 'antd';
import { NodeIndexOutlined, PlusOutlined, SyncOutlined, GlobalOutlined, InfoCircleOutlined, EyeOutlined, DeleteOutlined, EditOutlined, RocketOutlined, SendOutlined, StopOutlined } from '@ant-design/icons';
import apiClient from '../api';

const { Title, Text, Paragraph } = Typography;

const WhatsAppFlowManager = () => {
  const [form] = Form.useForm();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editorVisible, setEditorVisible] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [metadataModalVisible, setMetadataModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedFlowDetails, setSelectedFlowDetails] = useState(null);
  const [metadataForm] = Form.useForm();
  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [sendingFlowId, setSendingFlowId] = useState(null);
  const [sendForm] = Form.useForm();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  
  // Builder State
  const [screens, setScreens] = useState([]);
  const [currentScreenId, setCurrentScreenId] = useState(null);
  const [account, setAccount] = useState(null);
  const [selectedWaba, setSelectedWaba] = useState(null);

  useEffect(() => {
    loadAccountAndFlows();
  }, []);

  const loadAccountAndFlows = async () => {
    setLoading(true);
    try {
      const accRes = await apiClient.get('/accounts/me');
      if (accRes.data.success) {
        const acc = accRes.data.account;
        setAccount(acc);
        
        // Use the first active WABA if available
        const firstWaba = acc.whatsappNumbers?.find(n => n.isActive)?.wabaId || acc.cloudApiConfig?.businessAccountId;
        if (firstWaba) {
          setSelectedWaba(firstWaba);
          fetchFlows(firstWaba);
        }
      }
    } catch (err) {
      message.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFlows = async (wabaId) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/whatsapp/flows?wabaId=${wabaId}`);
      if (res.data.success) {
        // Deduplicate by ID to prevent React key errors
        const uniqueFlows = Array.from(new Map(res.data.flows.map(f => [f.id, f])).values());
        setFlows(uniqueFlows);
      }
    } catch (err) {
      message.error('Failed to fetch flows: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateFlowJson = () => {
    return JSON.stringify({
      version: "6.2",
      screens: screens.map(s => ({
        id: s.id,
        title: s.title,
        terminal: s.terminal || false,
        ...(s.terminal ? { success: s.success || true } : {}),
        data: {},
        layout: {
          type: "SingleColumnLayout",
          children: s.children.map(c => {
            const clean = {};
            // Copy basic props
            clean.type = c.type;
            if (c.name) clean.name = c.name;
            if (c.label) clean.label = c.label;
            if (c.required !== undefined) clean.required = c.required;

            // Type-specific kebab-case props
            if (c.type === 'TextHeading' || c.type === 'TextBody') {
              clean.text = c.text || "";
            }
            if (c.type === 'Dropdown') {
              clean['data-source'] = c.options || [];
            }
            if (c.type === 'TextInput') {
              clean['input-type'] = c.inputType || "text";
            }
            if (c.type === 'PhotoPicker' || c.type === 'MediaUpload') {
              clean.type = 'PhotoPicker'; // Standardized for 6.2
              clean['min-uploaded-photos'] = 1;
              clean['max-uploaded-photos'] = 1;
            }

            // Action kebab-case
            if (c.on_click_action || c.onClickAction) {
              const act = c.on_click_action || c.onClickAction;
              clean['on-click-action'] = {
                name: act.name,
                next: act.next,
                payload: act.payload || {}
              };
              // Remove empty payload for cleaner navigation
              if (act.name === 'navigate' && (!act.payload || Object.keys(act.payload).length === 0)) {
                delete clean['on-click-action'].payload;
              }
              // For complete action, payload is often required
              if (act.name === 'complete' && !clean['on-click-action'].payload) {
                clean['on-click-action'].payload = { status: "success" };
              }
            }
            
            return clean;
          })
        }
      }))
    }, null, 2);
  };

  const handleUploadAssets = async () => {
    const hide = message.loading('Syncing flow screens to Meta...', 0);
    try {
      setLoading(true);
      const finalJson = generateFlowJson();
      
      const res = await apiClient.post(`/whatsapp/flows/${editingFlow.id}/assets`, {
        wabaId: selectedWaba,
        flowJson: JSON.parse(finalJson)
      });
      
      if (res.data.success) {
        hide();
        message.success('Flow screens uploaded & synced successfully!');
        setEditorVisible(false);
        fetchFlows(selectedWaba);
      } else {
        hide();
        message.error('Meta Sync failed: ' + (res.data.message || 'Unknown error'));
      }
    } catch (err) {
      hide();
      console.error('Upload Error:', err);
      message.error('Upload failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePublishFlow = async (flowId) => {
    try {
      setLoading(true);
      const res = await apiClient.post(`/whatsapp/flows/${flowId}/publish`, { wabaId: selectedWaba });
      if (res.data.success) {
        message.success('Flow is now PUBLISHED and live!');
        fetchFlows(selectedWaba);
      }
    } catch (err) {
      message.error('Publish failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
   
  const handleCreateFlow = async (values) => {
    if (!selectedWaba) {
      return message.warning('Please select a WhatsApp Business Account (WABA) first');
    }
    try {
      setLoading(true);
      const res = await apiClient.post('/whatsapp/flows', {
        wabaId: selectedWaba,
        name: values.name,
        categories: [values.category]
      });
      if (res.data.success) {
        message.success('Flow metadata created! Status: DRAFT');
        setModalVisible(false);
        form.resetFields();
        fetchFlows(selectedWaba);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message;
      message.error('Creation failed: ' + errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFlow = async (flowId) => {
    Modal.confirm({
      title: 'Delete Flow',
      content: 'Are you sure you want to delete this DRAFT flow? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          const res = await apiClient.delete(`/whatsapp/flows/${flowId}?wabaId=${selectedWaba}`);
          if (res.data.success) {
            message.success('Flow deleted');
            fetchFlows(selectedWaba);
          }
        } catch (err) {
          message.error('Delete failed: ' + (err.response?.data?.message || err.message));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleDeprecateFlow = async (flowId) => {
    Modal.confirm({
      title: 'Deprecate Flow',
      content: 'This will mark the published flow as DEPRECATED. Users will no longer be able to open it. Continue?',
      okText: 'Deprecate',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          const res = await apiClient.post(`/whatsapp/flows/${flowId}/deprecate`, { wabaId: selectedWaba });
          if (res.data.success) {
            message.success('Flow deprecated successfully');
            fetchFlows(selectedWaba);
          }
        } catch (err) {
          message.error('Deprecate failed: ' + (err.response?.data?.message || err.message));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handlePreviewFlow = async (flowId) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/whatsapp/flows/${flowId}/preview?wabaId=${selectedWaba}&invalidate=false`);
      if (res.data.success && res.data.preview?.preview_url) {
        setPreviewUrl(res.data.preview.preview_url);
        setPreviewVisible(true);
      } else {
        message.warning('Preview not available. Upload flow JSON first.');
      }
    } catch (err) {
      message.error('Preview failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMetadata = async (values) => {
    try {
      setLoading(true);
      const res = await apiClient.put(`/whatsapp/flows/${editingFlow.id}`, {
        wabaId: selectedWaba,
        name: values.name,
        categories: values.categories,
        endpoint_uri: values.endpoint_uri
      });
      if (res.data.success) {
        message.success('Metadata updated');
        setMetadataModalVisible(false);
        fetchFlows(selectedWaba);
      }
    } catch (err) {
      message.error('Update failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (flowId) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/whatsapp/flows/${flowId}?wabaId=${selectedWaba}`);
      if (res.data.success) {
        setSelectedFlowDetails(res.data.flow);
        setDetailsModalVisible(true);
      }
    } catch (err) {
      message.error('Failed to fetch details: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendFlow = async (values) => {
    try {
      setLoading(true);
      const flowRecord = flows.find(f => f.id === sendingFlowId);
      const determinedMode = flowRecord?.status === 'PUBLISHED' ? 'published' : 'draft';
      
      const res = await apiClient.post(`/whatsapp/flows/${sendingFlowId}/send`, {
        wabaId: selectedWaba,
        to: values.to,
        flowCta: values.flowCta || 'Open Form',
        body: values.body || 'Please fill out this form',
        header: values.header,
        footer: values.footer,
        flowAction: values.flowAction || 'navigate',
        mode: determinedMode
      });
      if (res.data.success) {
        message.success('Flow message sent successfully!');
        setSendModalVisible(false);
        sendForm.resetFields();
      }
    } catch (err) {
      message.error('Send failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Flow Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Flow ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => <code style={{ color: '#eb2f96' }}>{id}</code>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'PUBLISHED' ? 'green' : status === 'DEPRECATED' ? 'red' : 'orange'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Category',
      dataIndex: 'categories',
      key: 'categories',
      render: (cats) => cats?.join(', ')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Tooltip title="View Health & Errors">
            <Button 
              icon={<InfoCircleOutlined />}
              onClick={() => handleViewDetails(record.id)}
            />
          </Tooltip>

          <Tooltip title="Edit Metadata">
            <Button 
              icon={<EditOutlined />}
              onClick={() => {
                setEditingFlow(record);
                metadataForm.setFieldsValue({
                  name: record.name,
                  categories: record.categories,
                  endpoint_uri: record.endpoint_uri
                });
                setMetadataModalVisible(true);
              }}
            />
          </Tooltip>

          <Button 
            type="primary"
            icon={<NodeIndexOutlined />}
            onClick={() => {
              setEditingFlow(record);
              setScreens([
                { 
                  id: "WELCOME", 
                  title: "Welcome", 
                  terminal: false,
                  children: [
                    { type: "TextHeading", text: "Welcome to our Service" },
                    { type: "TextBody", text: "Please fill out the form below to get started." },
                    { type: "Footer", label: "Continue", on_click_action: { name: "navigate", next: { type: "screen", name: "SUCCESS" } } }
                  ] 
                },
                {
                  id: "SUCCESS",
                  title: "Success",
                  terminal: true,
                  success: true,
                  children: [
                    { type: "TextHeading", text: "Thank You!" },
                    { type: "TextBody", text: "We have received your details." },
                    { type: "Footer", label: "Done", on_click_action: { name: "complete", payload: { status: "success" } } }
                  ]
                }
              ]);
              setCurrentScreenId("WELCOME");
              setEditorVisible(true);
            }}
          >Visual Builder</Button>

          <Tooltip title="Preview in iframe">
            <Button 
              icon={<EyeOutlined />}
              onClick={() => handlePreviewFlow(record.id)}
            />
          </Tooltip>

          <Tooltip title="Send to a number">
            <Button 
              type="primary"
              ghost
              icon={<SendOutlined />}
              onClick={() => {
                setSendingFlowId(record.id);
                setSendModalVisible(true);
              }}
            >Send</Button>
          </Tooltip>
          
          {record.status === 'DRAFT' && (
            <Button 
              type="primary" 
              ghost 
              icon={<RocketOutlined />}
              onClick={() => handlePublishFlow(record.id)}
            >Publish</Button>
          )}

          {record.status === 'PUBLISHED' && (
            <Tooltip title="Deprecate this published flow">
              <Button 
                danger
                ghost
                icon={<StopOutlined />}
                onClick={() => handleDeprecateFlow(record.id)}
              >Deprecate</Button>
            </Tooltip>
          )}

          {record.status === 'DRAFT' && (
            <Button 
              danger 
              type="text"
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteFlow(record.id)}
            />
          )}
        </Space>
      )
    }
  ];

  const addScreen = () => {
    const screenNames = ['FORM', 'DETAILS', 'CONFIRM', 'REVIEW', 'INFO', 'INPUT', 'SUBMIT', 'EXTRA'];
    const usedIds = screens.map(s => s.id);
    const newId = screenNames.find(n => !usedIds.includes(n)) || `SCREEN_${String.fromCharCode(65 + screens.length)}`;
    const newScreen = { 
      id: newId, 
      title: "New Screen", 
      children: [
        { type: "TextHeading", text: "New Screen" }, 
        { 
          type: "Footer", 
          label: "Submit", 
          on_click_action: { name: "complete", payload: { status: "success" } } 
        } 
      ] 
    };
    setScreens([...screens, newScreen]);
    setCurrentScreenId(newId);
  };

  const removeScreen = (e, screenId) => {
    e.stopPropagation();
    if (screens.length <= 1) {
      return message.warning('A Flow must have at least one screen.');
    }
    const newScreens = screens.filter(s => s.id !== screenId);
    setScreens(newScreens);
    if (currentScreenId === screenId) {
      setCurrentScreenId(newScreens[0].id);
    }
  };

  const addComponent = (type) => {
    const newScreens = [...screens];
    const screenIndex = newScreens.findIndex(s => s.id === currentScreenId);
    if (screenIndex === -1) return;

    let component = { type };
    if (type === 'TextHeading') component.text = "Heading Text";
    if (type === 'TextInput') { component.label = "Enter Text"; component.name = `input_${Date.now()}`; component.required = true; }
    if (type === 'Dropdown') { component.label = "Select Option"; component.name = `select_${Date.now()}`; component.options = [{ id: "opt1", title: "Option 1" }]; }
    if (type === 'MediaUpload') { component.label = "Upload File"; component.name = `file_${Date.now()}`; component.allowed_file_types = ["image/jpeg", "image/png", "application/pdf"]; }
    
    // Insert before footer if possible
    const children = [...newScreens[screenIndex].children];
    const footerIdx = children.findIndex(c => c.type === 'Footer');
    if (footerIdx !== -1) {
      children.splice(footerIdx, 0, component);
    } else {
      children.push(component);
    }
    
    newScreens[screenIndex].children = children;
    setScreens(newScreens);
  };

  const updateComponent = (idx, updates) => {
    const newScreens = [...screens];
    const screenIndex = newScreens.findIndex(s => s.id === currentScreenId);
    newScreens[screenIndex].children[idx] = { ...newScreens[screenIndex].children[idx], ...updates };
    setScreens(newScreens);
  };

  const removeComponent = (idx) => {
    const newScreens = [...screens];
    const screenIndex = newScreens.findIndex(s => s.id === currentScreenId);
    newScreens[screenIndex].children.splice(idx, 1);
    setScreens(newScreens);
  };

  const PhoneCanvas = ({ screen }) => (
    <div style={{ 
      width: '320px', 
      height: '600px', 
      background: '#fff', 
      borderRadius: '30px', 
      border: '8px solid #333', 
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Notch */}
      <div style={{ width: '150px', height: '25px', background: '#333', position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', borderBottomLeftRadius: '15px', borderBottomRightRadius: '15px', zIndex: 10 }}></div>
      
      {/* Flow Header */}
      <div style={{ background: '#00a884', color: '#fff', padding: '35px 20px 15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <GlobalOutlined />
        <Text style={{ color: '#fff', fontWeight: 'bold' }}>{screen?.title}</Text>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '20px', overflowY: 'auto', paddingBottom: '100px' }}>
        {screen?.children?.map((c, i) => (
          <div key={i} style={{ marginBottom: '15px' }}>
            {c.type === 'TextHeading' && <Title level={4} style={{ marginBottom: 4 }}>{c.text}</Title>}
            {c.type === 'TextBody' && <Paragraph style={{ fontSize: '14px', color: '#555' }}>{c.text}</Paragraph>}
            {c.type === 'TextInput' && (
              <div style={{ marginBottom: '15px' }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>{c.label}</Text>
                <Input 
                  placeholder={`Enter ${c.label}`}
                  style={{ width: '100%', borderBottom: '1px solid #ddd' }}
                  bordered={false}
                />
              </div>
            )}
            {c.type === 'Dropdown' && (
              <div style={{ marginBottom: '15px' }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>{c.label}</Text>
                <Select 
                  placeholder="Select Option"
                  style={{ width: '100%' }}
                  bordered={false}
                  className="mobile-select"
                  dropdownStyle={{ borderRadius: '8px' }}
                  options={c.options?.map(o => ({ label: o.title, value: o.id }))}
                  suffixIcon={<Text type="secondary">⌄</Text>}
                />
                <div style={{ borderBottom: '1px solid #eee', marginTop: '2px' }}></div>
              </div>
            )}
            {c.type === 'MediaUpload' && (
              <div style={{ marginBottom: '15px' }}>
                <Upload 
                  listType="picture-card" 
                  maxCount={1} 
                  showUploadList={false} 
                  style={{ width: '100%' }}
                >
                  <div style={{ textAlign: 'center', width: '100%', padding: '10px' }}>
                    <SyncOutlined style={{ color: '#00a884', fontSize: '20px', marginBottom: '8px' }} spin={loading} />
                    <br />
                    <Text strong style={{ color: '#00a884' }}>{c.label}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '10px' }}>TAP TO ATTACH {c.allowed_file_types?.includes('application/pdf') ? 'FILE' : 'IMAGE'}</Text>
                  </div>
                </Upload>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Interactive Footer Button */}
      {screen?.children?.find(c => c.type === 'Footer') && (() => {
        const footer = screen.children.find(c => c.type === 'Footer');
        return (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px', background: '#fff', borderTop: '1px solid #eee' }}>
            <Button 
              block 
              type="primary" 
              style={{ background: '#00a884', borderColor: '#00a884', borderRadius: '20px', height: '40px' }}
              onClick={() => {
                if (footer.on_click_action?.name === 'navigate') {
                  const nextScreen = footer.on_click_action.next?.name;
                  if (nextScreen) {
                    setCurrentScreenId(nextScreen);
                    message.info(`Navigating to ${nextScreen}`);
                  }
                } else if (footer.on_click_action?.name === 'complete') {
                  message.success('Flow Complete! (Simulation)');
                }
              }}
            >
              {footer.label}
            </Button>
          </div>
        );
      })()}
    </div>
  );

  const wabaOptions = account?.whatsappNumbers?.map(n => ({
    label: `${n.displayNumber} (${n.wabaId})`,
    value: n.wabaId
  })) || [];

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={2}><NodeIndexOutlined /> WhatsApp Flows</Title>
          <Paragraph type="secondary">
            Create and manage multi-screen interactive forms for Lead Capture, Booking, and Surveys.
          </Paragraph>
        </div>
        <Space>
          <Button icon={<SyncOutlined />} onClick={() => selectedWaba && fetchFlows(selectedWaba)} loading={loading} disabled={!selectedWaba}>Refresh</Button>
          <Tooltip title={!selectedWaba ? "Please select a WABA first" : ""}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} disabled={!selectedWaba}>Create Flow</Button>
          </Tooltip>
        </Space>
      </div>

      {!selectedWaba && (
        <Alert
          message="Action Required"
          description="Please select a WhatsApp Business Account (WABA) below to manage or create flows."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Card style={{ marginBottom: 24 }}>
        <Space>
          <Text strong>Select WABA Account:</Text>
          <Select 
            style={{ width: 300 }} 
            options={wabaOptions} 
            value={selectedWaba}
            onChange={(val) => {
              setSelectedWaba(val);
              fetchFlows(val);
            }}
            placeholder="Select a Business Account"
          />
        </Space>
      </Card>

      <Table 
        dataSource={flows} 
        columns={columns} 
        rowKey="id" 
        loading={loading}
        locale={{ emptyText: 'No flows found for this WABA.' }}
      />

      <Modal
        title="Create New WhatsApp Flow"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            handleCreateFlow(values);
          } catch (e) {
            // Validation failed
          }
        }}
        okText="Create"
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateFlow}>
          <Form.Item name="name" label="Flow Name" rules={[{ required: true, message: 'Give your flow a name' }, { pattern: /^[a-z0-9_]+$/, message: 'Only lowercase, numbers, and underscores allowed' }]}>
            <Input placeholder="e.g. sales_survey_form" />
          </Form.Item>
          <Form.Item name="category" label="Category" initialValue="LEAD_GENERATION">
            <Select placeholder="Select a category">
              <Select.Option value="SIGN_UP">Sign Up</Select.Option>
              <Select.Option value="SIGN_IN">Sign In</Select.Option>
              <Select.Option value="APPOINTMENT_BOOKING">Appointment Booking</Select.Option>
              <Select.Option value="LEAD_GENERATION">Lead Generation</Select.Option>
              <Select.Option value="CONTACT_US">Contact Us</Select.Option>
              <Select.Option value="CUSTOMER_SUPPORT">Customer Support</Select.Option>
              <Select.Option value="SURVEY">Survey</Select.Option>
              <Select.Option value="OTHER">Other</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Visual Flow Builder Modal */}
      <Modal
        title={null}
        open={editorVisible}
        onCancel={() => setEditorVisible(false)}
        footer={null}
        width="100%"
        style={{ top: 0, padding: 0 }}
        styles={{ body: { padding: 0, background: '#f5f7fa', height: '100vh' } }}
        closeIcon={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Top Bar */}
          <div style={{ height: '60px', background: '#fff', borderBottom: '1px solid #ddd', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <Button icon={<NodeIndexOutlined />} onClick={() => setEditorVisible(false)}>Back</Button>
              <Divider type="vertical" />
              <Title level={4} style={{ margin: 0 }}>Visual Flow Builder: {editingFlow?.name}</Title>
            </Space>
            <Space>
              <Button type="primary" onClick={handleUploadAssets} loading={loading} icon={<SyncOutlined />}>Save & Sync to Meta</Button>
            </Space>
          </div>

          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            {/* Sidebar: Screens */}
            <div style={{ width: '250px', background: '#fff', borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px', borderBottom: '1px solid #eee' }}>
                <Button block icon={<PlusOutlined />} onClick={addScreen}>Add Screen</Button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {screens.map(s => (
                  <div 
                    key={s.id} 
                    onClick={() => setCurrentScreenId(s.id)}
                    style={{ 
                      padding: '12px 16px', 
                      cursor: 'pointer', 
                      background: currentScreenId === s.id ? '#e6f7ff' : 'transparent',
                      borderLeft: currentScreenId === s.id ? '4px solid #1890ff' : '4px solid transparent',
                      borderBottom: '1px solid #f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <Text strong>{s.title}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>{s.id}</Text>
                    </div>
                    {screens.length > 1 && (
                      <Button 
                        type="text" 
                        danger 
                        size="small" 
                        icon={<DeleteOutlined />} 
                        onClick={(e) => removeScreen(e, s.id)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Main Canvas */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
              <PhoneCanvas screen={screens.find(s => s.id === currentScreenId)} />
            </div>

            {/* Properties Panel */}
            <div style={{ width: '350px', background: '#fff', borderLeft: '1px solid #ddd', overflowY: 'auto', padding: '24px' }}>
              <Title level={5}>Screen Settings</Title>
              <Form layout="vertical">
                <Form.Item label="Screen ID">
                  <Input value={currentScreenId} disabled />
                </Form.Item>
                <Form.Item label="Screen Title">
                  <Input 
                    value={screens.find(s => s.id === currentScreenId)?.title} 
                    onChange={(e) => {
                      const newScreens = [...screens];
                      const idx = newScreens.findIndex(s => s.id === currentScreenId);
                      newScreens[idx].title = e.target.value;
                      setScreens(newScreens);
                    }} 
                  />
                </Form.Item>

                <Divider />
                <Title level={5}>Components</Title>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '24px' }}>
                  <Button size="small" onClick={() => addComponent('TextHeading')}>+ Header</Button>
                  <Button size="small" onClick={() => addComponent('TextBody')}>+ Body Text</Button>
                  <Button size="small" onClick={() => addComponent('TextInput')}>+ Input Field</Button>
                  <Button size="small" onClick={() => addComponent('Dropdown')}>+ Dropdown</Button>
                  <Button size="small" onClick={() => addComponent('MediaUpload')}>+ File Upload</Button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {screens.find(s => s.id === currentScreenId)?.children?.map((c, i) => (
                    <Card 
                      key={i} 
                      size="small" 
                      title={`${c.type}`} 
                      extra={c.type !== 'Footer' && <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeComponent(i)} />}
                    >
                      {c.type.startsWith('Text') && (
                        <Input.TextArea value={c.text} onChange={(e) => updateComponent(i, { text: e.target.value })} />
                      )}
                      {(c.type === 'TextInput' || c.type === 'MediaUpload') && (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input value={c.label} addonBefore="Label" onChange={(e) => updateComponent(i, { label: e.target.value })} />
                          <Input value={c.name} addonBefore="Data Key" onChange={(e) => updateComponent(i, { name: e.target.value })} />
                          {c.type === 'MediaUpload' && (
                            <Checkbox.Group 
                              options={[
                                { label: 'Images (JPEG)', value: 'image/jpeg' },
                                { label: 'PNG', value: 'image/png' },
                                { label: 'PDF Docs', value: 'application/pdf' }
                              ]} 
                              value={c.allowed_file_types}
                              onChange={(val) => updateComponent(i, { allowed_file_types: val })}
                            />
                          )}
                        </Space>
                      )}
                      {c.type === 'Dropdown' && (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input value={c.label} addonBefore="Label" onChange={(e) => updateComponent(i, { label: e.target.value })} />
                          <Input value={c.name} addonBefore="Data Key" onChange={(e) => updateComponent(i, { name: e.target.value })} />
                          
                          <Divider style={{ margin: '8px 0' }} />
                          <Text strong size="small">Menu Options</Text>
                          {c.options?.map((opt, optIdx) => (
                            <Space key={optIdx} style={{ display: 'flex', marginBottom: 4 }}>
                              <Input 
                                placeholder="ID" 
                                style={{ width: 80 }} 
                                value={opt.id} 
                                onChange={(e) => {
                                  const newOpts = [...c.options];
                                  newOpts[optIdx].id = e.target.value;
                                  updateComponent(i, { options: newOpts });
                                }}
                              />
                              <Input 
                                placeholder="Label" 
                                value={opt.title} 
                                onChange={(e) => {
                                  const newOpts = [...c.options];
                                  newOpts[optIdx].title = e.target.value;
                                  updateComponent(i, { options: newOpts });
                                }}
                              />
                              <Button 
                                type="text" 
                                danger 
                                icon={<DeleteOutlined />} 
                                onClick={() => {
                                  const newOpts = c.options.filter((_, idx) => idx !== optIdx);
                                  updateComponent(i, { options: newOpts });
                                }}
                              />
                            </Space>
                          ))}
                          <Button 
                            block 
                            size="small" 
                            type="dashed" 
                            icon={<PlusOutlined />}
                            onClick={() => {
                              const newOpts = [...(c.options || []), { id: `opt_${Date.now()}`, title: "New Option" }];
                              updateComponent(i, { options: newOpts });
                            }}
                          >Add Option</Button>
                        </Space>
                      )}
                      {c.type === 'Footer' && (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Input value={c.label} addonBefore="Label" onChange={(e) => updateComponent(i, { label: e.target.value })} />
                          <Select 
                            value={c.on_click_action?.name} 
                            style={{ width: '100%' }}
                            onChange={(val) => updateComponent(i, { on_click_action: { ...c.on_click_action, name: val } })}
                            options={[
                              { label: 'Navigate (Next Screen)', value: 'navigate' },
                              { label: 'Complete (Submit)', value: 'complete' }
                            ]}
                          />
                          {c.on_click_action?.name === 'navigate' && (
                            <Select 
                              value={c.on_click_action?.next?.name} 
                              placeholder="Select Next Screen"
                              style={{ width: '100%' }}
                              onChange={(val) => updateComponent(i, { on_click_action: { ...c.on_click_action, next: { type: "screen", name: val } } })}
                              options={screens.filter(s => s.id !== currentScreenId).map(s => ({ 
                                label: `${s.title} (${s.id})`, 
                                value: s.id 
                              }))}
                            />
                          )}
                        </Space>
                      )}
                    </Card>
                  ))}
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Metadata Modal */}
      <Modal
        title="Edit Flow Metadata"
        open={metadataModalVisible}
        onCancel={() => setMetadataModalVisible(false)}
        onOk={() => metadataForm.submit()}
        confirmLoading={loading}
      >
        <Form form={metadataForm} layout="vertical" onFinish={handleUpdateMetadata}>
          <Form.Item name="name" label="Flow Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. lead_capture_form" />
          </Form.Item>
          <Form.Item name="categories" label="Categories" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select categories">
              <Select.Option value="SIGN_UP">Sign Up</Select.Option>
              <Select.Option value="SIGN_IN">Sign In</Select.Option>
              <Select.Option value="APPOINTMENT_BOOKING">Appointment Booking</Select.Option>
              <Select.Option value="LEAD_GENERATION">Lead Generation</Select.Option>
              <Select.Option value="CONTACT_US">Contact Us</Select.Option>
              <Select.Option value="CUSTOMER_SUPPORT">Customer Support</Select.Option>
              <Select.Option value="SURVEY">Survey</Select.Option>
              <Select.Option value="OTHER">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="endpoint_uri" label="Endpoint URL (for Dynamic Data Exchange)">
            <Input placeholder="https://your-api.com/whatsapp/flows/callback" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Flow Details / Health Modal */}
      <Modal
        title="Flow Details & Health Status"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedFlowDetails && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert 
              message={`Status: ${selectedFlowDetails.status}`}
              type={selectedFlowDetails.status === 'PUBLISHED' ? 'success' : 'info'}
              showIcon
            />
            
            <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '8px' }}>
              <Text strong>Health Status: </Text>
              <Tag color={selectedFlowDetails.health_status?.can_send_message === 'AVAILABLE' ? 'green' : 'red'}>
                {selectedFlowDetails.health_status?.can_send_message || 'UNKNOWN'}
              </Tag>
              {selectedFlowDetails.health_status?.entities?.map(entity => (
                <div key={entity.id} style={{ marginTop: 8, fontSize: '12px' }}>
                  <Text type="secondary">{entity.entity_type} ({entity.id}): </Text>
                  <Tag size="small" color={entity.can_send_message === 'AVAILABLE' ? 'green' : 'red'}>{entity.can_send_message}</Tag>
                  {entity.errors?.map((err, idx) => (
                    <div key={idx} style={{ color: 'red', marginLeft: 10 }}>• {err.error_description}</div>
                  ))}
                </div>
              ))}
            </div>

            {selectedFlowDetails.validation_errors?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text strong type="danger">JSON Validation Errors:</Text>
                {selectedFlowDetails.validation_errors.map((err, idx) => (
                  <Alert 
                    key={idx}
                    style={{ marginTop: 8 }}
                    type="error"
                    message={err.message}
                    description={`Line ${err.line_start}, Column ${err.column_start}`}
                  />
                ))}
              </div>
            )}

            <Divider />
            <Space direction="vertical">
              <Text><Text strong>JSON Version:</Text> {selectedFlowDetails.json_version}</Text>
              <Text><Text strong>Endpoint:</Text> {selectedFlowDetails.endpoint_uri || 'None'}</Text>
              <Text><Text strong>Connected App:</Text> {selectedFlowDetails.application?.name} ({selectedFlowDetails.application?.id})</Text>
            </Space>
          </Space>
        )}
      </Modal>

      {/* Send Flow Modal */}
      <Modal
        title={`Send Flow ${sendingFlowId ? `(${sendingFlowId})` : ''}`}
        open={sendModalVisible}
        onCancel={() => { setSendModalVisible(false); sendForm.resetFields(); }}
        onOk={() => sendForm.submit()}
        okText="Send"
        confirmLoading={loading}
      >
        <Form form={sendForm} layout="vertical" onFinish={handleSendFlow}>
          <Form.Item name="to" label="Recipient Phone Number" rules={[{ required: true, message: 'Enter the recipient phone number with country code' }]}>
            <Input placeholder="e.g. 919876543210" />
          </Form.Item>
          <Form.Item name="flowCta" label="CTA Button Text" initialValue="Open Form">
            <Input placeholder="e.g. Open Form, Book Now, Fill Survey" />
          </Form.Item>
          <Form.Item name="body" label="Message Body" initialValue="Please fill out this form">
            <Input.TextArea rows={2} placeholder="Body text shown in the chat bubble" />
          </Form.Item>
          <Form.Item name="header" label="Header Text (Optional)">
            <Input placeholder="e.g. Lead Capture Form" />
          </Form.Item>
          <Form.Item name="footer" label="Footer Text (Optional)">
            <Input placeholder="e.g. Powered by our system" />
          </Form.Item>
          <Form.Item name="flowAction" label="Flow Action" initialValue="navigate">
            <Select options={[
              { label: 'Navigate (show first screen)', value: 'navigate' },
              { label: 'Data Exchange (endpoint)', value: 'data_exchange' }
            ]} />
          </Form.Item>
          <Form.Item name="mode" label="Mode (Optional)">
            <Select allowClear placeholder="Leave empty for published flows" options={[
              { label: 'Draft (test unpublished)', value: 'draft' },
            ]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="Flow Preview"
        open={previewVisible}
        onCancel={() => { setPreviewVisible(false); setPreviewUrl(null); }}
        footer={null}
        width={500}
        styles={{ body: { padding: 0, height: '700px' } }}
      >
        {previewUrl && (
          <iframe 
            src={previewUrl} 
            width="100%" 
            height="700" 
            style={{ border: 'none', borderRadius: '8px' }}
            title="WhatsApp Flow Preview"
          />
        )}
      </Modal>
    </div>
  );
};

export default WhatsAppFlowManager;
